import {
  type Ref,
  createContext,
  useContext,
  useRef,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
} from 'react';
import i18n from '@/Config/app/i18n';
import { WidgetThemeTransparencyStyle } from '@/Domain/api/WIdget/dto/WidgetTheme.dto';

type Props = {
  iframe: Ref<HTMLIFrameElement>;
  iframeElement: JSX.Element | null;
  setIframeElement: (element: JSX.Element) => void;
  setConfiguration: (property: string, value: any) => void;
  refreshConfiguration: () => void;
  reset: () => void;
  metadata: string;
  setMetadata: (metadata: string) => void;
  getValidatedMetadata: () => Record<string, any> | null;
  bubbleViewRequestId: number;
  requestBubbleView: () => void;
  // The Background style last pushed to the preview, before any save; null until one is pushed.
  previewTransparencyStyle: WidgetThemeTransparencyStyle | null;
};

export const PreviewContext = createContext<Props | null>(null);

export const usePreview = (): Props => {
  const context = useContext(PreviewContext);
  if (context === null) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};

const getDefaultMetadata = (): string => {
  const language = i18n.language || 'en';
  const fullPathURL = typeof window !== 'undefined' ? window.location.href : '';
  return JSON.stringify({ language, fullPathURL }, null, 2);
};

export function PreviewProvider({ children }: PropsWithChildren): JSX.Element {
  const iframe = useRef<HTMLIFrameElement>(null);
  const [iframeElement, setIframeElement] = useState<JSX.Element | null>(null);
  const [metadata, setMetadata] = useState<string>(getDefaultMetadata());
  const [bubbleViewRequestId, setBubbleViewRequestId] = useState(0);
  const requestBubbleView = useCallback(() => setBubbleViewRequestId((id) => id + 1), []);
  const [previewTransparencyStyle, setPreviewTransparencyStyle] =
    useState<WidgetThemeTransparencyStyle | null>(null);

  const setConfiguration = useCallback((property: string, value: any): void => {
    iframe.current?.contentWindow?.postMessage(
      { type: 'quickchat_change', data: { property, value } },
      '*',
    );
    if (property === 'widget_theme_custom') {
      setPreviewTransparencyStyle(value?.transparency?.style ?? 'none');
    }
  }, []);

  const refreshConfiguration = useCallback((): void => {
    iframe.current?.contentWindow?.postMessage({ type: 'quickchat_refresh_configuration' }, '*');
  }, []);

  const getValidatedMetadata = useCallback((): Record<string, any> | null => {
    if (!metadata.trim()) {
      return null;
    }
    try {
      const parsed = JSON.parse(metadata);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }, [metadata]);

  const reset = useCallback((): void => {
    if (iframe?.current != null) {
      // eslint-disable-next-line no-self-assign
      iframe.current.src = iframe.current.src;
    }
  }, []);

  const value = useMemo(
    () => ({
      iframe,
      iframeElement,
      setIframeElement,
      setConfiguration,
      refreshConfiguration,
      reset,
      metadata,
      setMetadata,
      getValidatedMetadata,
      bubbleViewRequestId,
      requestBubbleView,
      previewTransparencyStyle,
    }),
    [
      iframeElement,
      setConfiguration,
      refreshConfiguration,
      reset,
      metadata,
      getValidatedMetadata,
      bubbleViewRequestId,
      requestBubbleView,
      previewTransparencyStyle,
    ],
  );

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}
